import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { add, format } from "date-fns";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSharedValue } from "react-native-reanimated";
import { useFragment } from "react-relay";

import { ScheduleOption } from "@/components/ScheduleSwitcher";
import { useGetBookingWindowDaysByFacilityId } from "@/hooks/useGetBookingWindowDaysByFacilityId";
import { setLastSelectedBookingSelection } from "@/utils/bookingSelectionStorage";
import { AgendaLayout } from "@whoosh/design-system/src/apps/PatronApp/layout/AgendaLayout";
import { SignedOutBanner } from "@whoosh/design-system/src/apps/PatronApp/layout/SignedOutBanner";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { Box, Text } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { ClubStackNavigatorParamList } from "../../navigators/ClubNavigator";
import { useFlags } from "../../utils/launchDarkly";
import { WaitlistModal } from "../Agenda/components/WaitlistModal";
import { useInitializeStoredCourseIds } from "../DurationBasedAgendas/hooks/useInitializeStoredCourseIds";
import { useInitializeStoredGroupSizeFilter } from "../DurationBasedAgendas/hooks/useInitializeStoredGroupSizeFilter";
import { useInitializeStoredHoleCountFilter } from "../DurationBasedAgendas/hooks/useInitializeStoredHoleCountFilter";
import { useInitializeStoredTimeOfDayFilters } from "../DurationBasedAgendas/hooks/useInitializeStoredTimeOfDayFilters";
import { PatronBookingRateChangeConfirmModal } from "../PatronBookingRateChangeConfirmModal/PatronBookingRateChangeConfirmModal";
import { TeeSheet_HeaderData$key } from "./__generated__/TeeSheet_HeaderData.graphql";
import {
  maxSelectedTeeTimesAtom,
  selectedTeeTimesAtom,
  teeSheetGroupSizeFilterAtom,
  teeSheetHoleFilterAtom,
  teeSheetTimesAtom,
} from "./atoms";
import { teeSheetSelectedCourseIdsByFacility } from "./atoms/TeeSheetSelectedCourseIdsByFacility";
import { TeeSheetAgenda } from "./components/TeeSheeAgenda/TeeSheetAgenda";
import { TeeSheetAgendaHeaderNew } from "./components/TeeSheetAgendaHeaderNew/TeeSheetAgendaHeaderNew";
import { TeeSheetAgendaSidebar } from "./components/TeeSheetAgendaSidebar/TeeSheetAgendaSidebar";
import { TeeSheetBasedFiltersModal } from "./components/TeeSheetBasedFiltersModal";
import { useReserveMultipleTimesNewFlow } from "./hooks/useReserveMultipleTimesNewFlow";
import { useResetTeeSheetFilters } from "./hooks/useResetTeeSheetFilters";

type TeeSheetProps = {
  memberRef: TeeSheet_HeaderData$key;
  currentSlotId?: string;
  currentCourseId?: string;
  handleRowPress: (slotId: string) => void;
  onJoinableRowPress: (slotId: string) => void;
  dateOverride?: string;
  handleRequestedRowPress: (queueSlotId: string, time: string) => void;
  isAuthModalOpen?: boolean;
  setIsAuthModalOpen?: (val: boolean) => void;
};

export const TeeSheet = ({
  memberRef,
  currentSlotId,
  handleRowPress,
  onJoinableRowPress,
  currentCourseId,
  dateOverride,
  handleRequestedRowPress,
  isAuthModalOpen,
  setIsAuthModalOpen,
}: TeeSheetProps) => {
  const session = useFragment(TeeSheetHeaderFragmentString, memberRef);
  const member = session.currentClientProfile?.member;
  const isSignedIn = !!member?.id;

  const [isRateChangeConfirmModalOpen, setIsRateChangeConfirmModalOpen] =
    useState(false);

  const [selectedTeeTimes] = useAtom(selectedTeeTimesAtom);
  const [maxSelectedTeeTimes, setMaxSelectedTeeTimes] = useAtom(
    maxSelectedTeeTimesAtom
  );

  const isMobileScreen = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: false,
    eventCardColumn: false,
    lg: false,
    xl: false,
  });

  const {
    courseId,
    facilitySlug,
    date: currentlySelectedDate,
  } = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>().params;

  const facility =
    member?.club?.facilities?.edges && member.club.facilities.edges.length
      ? member.club.facilities.edges.find((e) => e?.node?.slug === facilitySlug)
      : null;
  const facilityNode = facility?.node ?? null;

  const isSingleCourseFacility = facility?.node?.courses?.edges?.length === 1;

  const [selectedCourseIds, _setSelectedCourseIds] = useAtom(
    teeSheetSelectedCourseIdsByFacility(facility?.node?.id ?? "")
  );
  const setTeeSheetGroupSizeFilter = useSetAtom(teeSheetGroupSizeFilterAtom);
  const setTeeSheetHoleFilter = useSetAtom(teeSheetHoleFilterAtom);
  const setTeeSheetTimes = useSetAtom(teeSheetTimesAtom);

  const setSelectedCourseIds = useCallback(
    (courseIds: string[]) => {
      const facilityCourses =
        facility?.node?.courses?.edges?.flatMap((e) =>
          e?.node?.id ? [e?.node?.id] : []
        ) || [];

      if (courseIds.length) {
        const facilityFilteredCourses = courseIds.filter((courseId) =>
          facilityCourses.includes(courseId)
        );
        if (facilityFilteredCourses.length) {
          _setSelectedCourseIds(facilityFilteredCourses);
        }
      } else {
        _setSelectedCourseIds(courseIds);
      }
    },
    [_setSelectedCourseIds, facility]
  );

  // Handle setting the courseIds filter based on several scenarios
  useEffect(() => {
    if (courseId && facility?.node?.id) {
      setLastSelectedBookingSelection({
        facilityId: facility.node.id,
        courseId,
      });
    }

    if (currentCourseId) {
      // pass in a course id to component (move booking)
      setSelectedCourseIds([currentCourseId]);
    } else if (courseId && !selectedCourseIds?.includes(courseId)) {
      // get a course id from the route params (tee sheet)
      setSelectedCourseIds([courseId]);
    } else if (
      (facility && facility.node?.courses?.edges && !selectedCourseIds) ||
      (isSingleCourseFacility && selectedCourseIds?.length === 0)
    ) {
      // course Ids have not been initialized
      setSelectedCourseIds(
        facility.node.courses.edges.flatMap((e) =>
          e?.node?.id ? [e?.node?.id] : []
        )
      );
    }
  }, [
    courseId,
    currentCourseId,
    facility,
    isSingleCourseFacility,
    selectedCourseIds,
    setSelectedCourseIds,
  ]);

  const storedFiltersFacilityId = facilityNode?.id ?? null;

  useInitializeStoredCourseIds(
    storedFiltersFacilityId,
    selectedCourseIds || [],
    setSelectedCourseIds
  );
  useInitializeStoredGroupSizeFilter(
    storedFiltersFacilityId,
    (value?: number) => setTeeSheetGroupSizeFilter(value || 1)
  );
  useInitializeStoredHoleCountFilter(
    storedFiltersFacilityId,
    setTeeSheetHoleFilter
  );
  useInitializeStoredTimeOfDayFilters(
    null,
    null,
    storedFiltersFacilityId,
    setTeeSheetTimes
  );

  useEffect(() => {
    setMaxSelectedTeeTimes(facility?.node?.maxMultipleBookings || 0);
  }, [facility?.node?.maxMultipleBookings, setMaxSelectedTeeTimes]);

  const courseFilterLabel = useMemo(() => {
    const courseNodes =
      facility?.node?.courses?.edges?.flatMap((edge) =>
        edge?.node ? [edge.node] : []
      ) || [];

    if (!courseNodes.length) {
      return null;
    }

    const normalizedSelectedCourseIds = selectedCourseIds ?? [];
    const allCoursesSelected =
      normalizedSelectedCourseIds.length === courseNodes.length;

    if (allCoursesSelected) {
      return t`All Courses`;
    }

    if (!normalizedSelectedCourseIds.length) {
      return null;
    }

    const selectedCourseNames = courseNodes
      .filter((course) => normalizedSelectedCourseIds.includes(course.id))
      .map((course) => course.name)
      .filter((name): name is string => Boolean(name));

    if (!selectedCourseNames.length) {
      return null;
    }

    return selectedCourseNames.join(", ");
  }, [facility?.node?.courses?.edges, selectedCourseIds]);

  const date = dateOverride || currentlySelectedDate;
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const bookingNavigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("Home", { screen: "MainHome" });
    }
  };

  const curDate = new Date();
  const todayString = format(curDate, "yyyy-MM-dd");
  const bookingWindowDays = useGetBookingWindowDaysByFacilityId({
    fragmentRef: facilityNode,
  });
  const bookingWindowEndDate = format(
    add(new Date(), { days: bookingWindowDays }),
    "yyyy-MM-dd"
  );
  const clubWeekStartsOn = 1;
  /**
   * Allows "today" as a string to be passed as the date and correctly converted
   * also always setting it here ensures that the date is always correctly set for TimeSlotList
   */
  useEffect(() => {
    if (date === "today") {
      navigation.setParams({ date: todayString });
    } else {
      navigation.setParams({ date });
    }
  }, [date, navigation, todayString]);

  const selectedDate = date === "today" ? todayString : date;

  const currentSelectedFacility: ScheduleOption = {
    type: "facility",
    id: facility?.node?.id || "",
    slug: facility?.node?.slug || "",
    label: facility?.node?.name || "",
    departmentType: facility?.node?.department?.type || "GOLF",
  };
  const selectLabel =
    currentSelectedFacility.departmentType === "GOLF"
      ? t`Select Tee Time`
      : t`Select Time`;

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isWaitlistModalOpen, setWaitlistModalOpen] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);
  const scrollY = useSharedValue(0);

  const shouldShowFooter = selectedTeeTimes.length > 0;
  const selectedText = `${selectedTeeTimes.length} ${
    selectedTeeTimes.length > 1 ? t`times selected` : t`time selected`
  }`;
  const maximumText = `(${maxSelectedTeeTimes} ${t`maximum`})`;

  const navigateToSummaryMultipleTeeTimes = () => {
    bookingNavigation.navigate("BookingSummary", {
      slotOrBookingId: "multiple-tee-times",
      params: { slotIds: selectedTeeTimes },
    });
  };

  const { reserveMultipleTimesNewFlow, isLocking, isCreating } =
    useReserveMultipleTimesNewFlow(
      selectedTeeTimes,
      member?.id || "",
      navigateToSummaryMultipleTeeTimes
    );

  const memberId = member?.id || "";

  const membershipTypeId = member?.membershipType?.id || "";

  const clientClass = member?.clientClass || "CUSTOMER";

  const resetTeeSheetFilters = useResetTeeSheetFilters(
    session?.currentClientProfile?.member?.club || null
  );

  // this resets the filters when this component unloads only.
  // the use effect must not have anything in its array or the filters will reset on every render.
  useEffect(() => {
    return () => resetTeeSheetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    waitlistRestrictions,
    memAppWaitlist: memAppWaitlistFeatureFlag,
    newTopbar,
  } = useFlags();

  const isMemberOnAnotherClub = !!session.user?.id && !member?.id;
  const onLoginAction = () => setIsAuthModalOpen?.(true);
  const signedOutTitle = isMemberOnAnotherClub
    ? t`Join now to avoid missing your times.`
    : t`Sign in to avoid missing your times.`;
  const signedOutSubtitle = isMemberOnAnotherClub
    ? t`You have not joined this club yet.`
    : t`You are signed out`;
  const signedOutButtonText = isMemberOnAnotherClub ? t`Join Club` : t`Sign In`;
  const hasWaitlistRestrictions =
    waitlistRestrictions &&
    !!facility?.node?.waitlistRestrictions?.edges?.length;

  const showWaitlist =
    memAppWaitlistFeatureFlag &&
    date &&
    facility?.node?.id &&
    (clientClass === "MEMBER" || hasWaitlistRestrictions);

  return (
    <>
      {isCreating || isLocking ? (
        <Loader />
      ) : (
        <>
          <AgendaLayout
            shouldCenter={true}
            offsetTop={isMobileScreen ? true : false}
            header={
              member?.club && date ? (
                <TeeSheetAgendaHeaderNew
                  isMobileScreen={isMobileScreen ?? false}
                  isScrolled={isScrolled}
                  onFilterPress={() => setIsFilterModalOpen(true)}
                  onBackPress={isSignedIn ? handleBackPress : undefined}
                  title={selectLabel}
                  courseFilterLabel={courseFilterLabel}
                  onWaitlistPress={() => setWaitlistModalOpen(true)}
                  clubDesktopRef={member?.club}
                  clubMobileRef={member?.club}
                  scrollY={scrollY}
                  startDate={todayString}
                  endDate={bookingWindowEndDate}
                  selectedDate={selectedDate || todayString}
                  showWaitlist={showWaitlist}
                  onDateChange={(newDate) => {
                    navigation.setParams({ date: newDate });
                  }}
                  clubWeekStartsOn={clubWeekStartsOn}
                />
              ) : (
                <Box />
              )
            }
            sidebarContent={
              <>
                {facility?.node && !isMobileScreen ? (
                  <TeeSheetAgendaSidebar
                    facilityRef={facility?.node}
                    sessionRef={session}
                    showWaitlist={showWaitlist}
                    dateOverride={date}
                    selectedCourseIds={selectedCourseIds || []}
                    setSelectedCourseIds={setSelectedCourseIds}
                    selectedDate={
                      currentlySelectedDate || format(new Date(), "yyyy-MM-dd")
                    }
                  />
                ) : null}
              </>
            }
            agenda={
              <TeeSheetAgenda
                selectedCourseIds={selectedCourseIds}
                facilityId={facility?.node?.id || ""}
                handleRowPress={handleRowPress}
                onJoinableRowPress={onJoinableRowPress}
                handleRequestedRowPress={handleRequestedRowPress}
                currentSlotId={currentSlotId}
                memberId={memberId}
                membershipTypeId={membershipTypeId}
                clientClass={clientClass}
                setIsScrolled={setIsScrolled}
                scrollY={scrollY}
                shouldShowFooter={shouldShowFooter}
                selectedText={selectedText}
                maximumText={maximumText}
                onReserveMultipleTimes={reserveMultipleTimesNewFlow}
                isSignedIn={isSignedIn}
                isMobileScreen={isMobileScreen}
                title={signedOutTitle}
                subtitle={signedOutSubtitle}
                buttonText={signedOutButtonText}
                onLoginAction={onLoginAction}
              />
            }
          />
          {isMobileScreen && showWaitlist ? (
            <WaitlistModal
              isOpen={isWaitlistModalOpen}
              onClose={() => setWaitlistModalOpen(false)}
              facilityId={facility?.node?.id || ""}
              date={date || ""}
              courseIds={selectedCourseIds}
              fragmentRef={facility?.node}
              onJoinWaitlist={handleRequestedRowPress}
            />
          ) : null}

          {selectedCourseIds && facility?.node && isMobileScreen ? (
            <TeeSheetBasedFiltersModal
              facilityRef={facility?.node}
              sessionRef={session}
              selectedCourseIds={selectedCourseIds}
              setSelectedCourseIds={setSelectedCourseIds}
              showWaitlist={showWaitlist}
              isOpen={isFilterModalOpen}
              onClose={() => setIsFilterModalOpen(false)}
            />
          ) : null}

          <PatronBookingRateChangeConfirmModal
            isOpen={isRateChangeConfirmModalOpen}
            onClose={() => setIsRateChangeConfirmModalOpen(false)}
          />
          {!newTopbar && !isSignedIn && isMobileScreen ? (
            <SignedOutBanner
              position="bottom"
              isOpen={!isSignedIn}
              title={signedOutTitle}
              subTitle={signedOutSubtitle}
              actionButton={
                <Button
                  variant="white"
                  size="tiny"
                  accessibilityLabel={signedOutButtonText}
                  onPress={onLoginAction}
                >
                  <Text variant="mainTiny" color="text-primary">
                    {signedOutButtonText}
                  </Text>
                </Button>
              }
            />
          ) : null}
        </>
      )}
    </>
  );
};

const TeeSheetHeaderFragmentString = graphql`
  fragment TeeSheet_HeaderData on Session {
    ...TeeSheetAgendaSidebar_session
    ...TeeSheetBasedFiltersModal_session
    user {
      ... on MemberUser {
        id
        email
      }
    }
    currentClientProfile {
      member {
        ... on Customer {
          id
        }
        ... on Member {
          id
        }
        membershipType {
          id
        }
        clientClass
        club {
          id
          ...TeeSheetAgendaHeaderDesktopNewFragment_club
          ...TeeSheetAgendaHeaderMobileNewFragment_club
          ...useResetTeeSheetFilters_club
          facilities {
            edges {
              node {
                id
                name
                slug
                maxMultipleBookings
                multipleBookingsEnabled
                ...useGetBookingWindowDaysByFacilityId
                ...getFacilityMemberPermissions_facility
                ...WaitlistModalFragment_Facility
                ...TeeSheetAgendaSidebarFragment_facility
                ...TeeSheetBasedFiltersModalFragment_facility
                ...AgendaDateSelectorControlFragment_facility
                courses {
                  edges {
                    node {
                      name
                      id
                    }
                  }
                }
                department {
                  type
                }
                waitlistRestrictions: bookingRules(type: [WAITLIST]) {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
